<!--

	This component replaces the deprecated `v-promise-btn` package, handling asynchronous
	button actions by showing a loading state and locking the button until the operation
	completes. It prevents multiple submissions by disabling further clicks while in progress.

	The `callback` prop triggers the action, and a Vuetify circular loading indicator is
	displayed during execution, offering better flexibility and customization compared to
	the old `v-promise-btn`.

-->
<template>
	<a class="mia-PromiseButton" :class="{ loading, disabled: disabled || loading, 'mia-PromiseButton--reverse': placement == 'right' }" @click="onClick">
		<slot name="loading">
			<v-progress-circular v-if="loading && !$slots['loading']" v-bind="progressCircularOptions" indeterminate />
		</slot>
		<slot />
	</a>
</template>
<script lang="ts">
import type { PropType } from "vue";
import { VProgressCircular } from "vuetify/components";

export default {
	name: "PromiseButton",

	components: {
		VProgressCircular,
	},

	props: {
		callback: {
			type: Function as PropType<() => Promise<void>>,
			required: true,
		},
		disabled: {
			type: Boolean,
			required: false,
			default: false,
		},
		placement: {
			type: String as () => "right" | "left",
			required: false,
			default: "left",
		},
		progressCircularOptions: {
			type: Object,
			required: false,
			default: {
				size: 18,
				width: 2,
			},
		},
	},

	data() {
		return {
			loading: false,
		};
	},

	methods: {
		async onClick() {
			if (this.loading || this.disabled) {
				return;
			}

			this.loading = true;

			try {
				await this.callback();
			}
			finally {
				this.loading = false;
			}
		},
	},
};
</script>
<style lang="scss">
a.mia-PromiseButton {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: theme.$mia-gap-size;

	&--reverse {
		flex-direction: row-reverse;
	}

	&.loading {
		.mia-Icon {
			display: none;
		}
	}

	&.disabled {
		pointer-events: unset;
	}
}
</style>
